import * as React from 'react';
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	Link,
	ListSubheader,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import { AutocompleteInput, required, TextInput } from 'react-admin';
import { ConversationState } from './WSCommunicationTypes';
import Chat from './Chat';
import { ACTUAL_VALUES, SELECTORS, toDisplayName } from './constants';
import {
	CRAWLING_METHOD_VALUES,
	DEFAULT_MODEL,
	SchemaSelectorFinderSession
} from './useSchemaSelectorFinderSession';

type SchemaSelectorFinderStepProps = {
	session: SchemaSelectorFinderSession;
};

export const SchemaSelectorFinderStep = ({
	session
}: SchemaSelectorFinderStepProps) => {
	const {
		productUrl,
		aiModel,
		canSearch,
		chatMessages,
		conversationState,
		crawlingMethod,
		customAIModel,
		getSelectedValue,
		handlePauseConversation,
		handleSearch,
		handleUserMessage,
		isGettingSomeSelectorValue,
		isSearching,
		selectorHistory,
		setAIModel,
		setCrawlingMethod,
		setCustomAIModel,
		setProductUrl,
		setSelector,
		setUseAI,
		testAllSelectors,
		handleDownloadHtml,
		useAI,
		isDownloadingHtml,
		availableModels,
	} = session;

	return (
		<>
			<Typography
				variant='h6'
				gutterBottom
				sx={{ borderBottom: '1px solid', marginBottom: '25px' }}
			>
				Auf "Selektoren Suchen" klicken, um automatisch Schema
				Selektoren zu finden.
			</Typography>
			<Stack spacing={2}>
				<Stack
					spacing={2}
					direction='row'
					sx={{ display: 'flex', justifyContent: 'space-between' }}
				>
					<Stack sx={{ flexGrow: 1 }}>
						<TextInput
							validate={[required()]}
							sx={{ maxWidth: 'sm' }}
							label='Produkt URL'
							source='productUrl'
							type='url'
							disabled={isSearching}
							onChange={(e) => setProductUrl(e.target.value)}
						/>
						<FormControl fullWidth>
							<InputLabel id='crawling-method-label'>
								Crawling-Methode
							</InputLabel>
							<Select
								id='crawling-method'
								value={crawlingMethod}
								label='Crawling-Methode'
								labelId='crawling-method-label'
								onChange={(e) =>
									setCrawlingMethod(e.target.value)
								}
								sx={{ maxWidth: 'sm' }}
								disabled={isSearching}
							>
								{CRAWLING_METHOD_VALUES.map((method) => (
									<MenuItem key={method} value={method}>
										{method || 'curl'}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
					<Stack
						sx={{
							display: 'flex',
							justifyContent: 'flex-end'
						}}
					>
						<Button onClick={() => handleDownloadHtml()} disabled={!productUrl}>
							Download HTML{' '}
							{isDownloadingHtml && (
								<CircularProgress size={20} sx={{ ml: 2 }} />
							)}
						</Button>
					</Stack>
				</Stack>
				<FormControlLabel
					control={
						<Checkbox
							checked={useAI}
							onChange={(e) => setUseAI(e.target.checked)}
							disabled={isSearching}
						/>
					}
					label='KI zur Selektor-Suche verwenden'
				/>
				{useAI && (
					<>
						<Stack direction='row' spacing={2}>
							<FormControl fullWidth>
								<InputLabel id='ai-model-label'>
									KI-Modell
								</InputLabel>
								<Select
									id='ai-model'
									value={aiModel}
									labelId='ai-model-label'
									label='KI-Modell'
									onChange={(e) => setAIModel(e.target.value)}
									sx={{ maxWidth: 'sm' }}
									disabled={isSearching}
								>
									<MenuItem
										value={DEFAULT_MODEL}
									>{`Default (${DEFAULT_MODEL})`}</MenuItem>
									<MenuItem value='other'>
										Anderes Modell angeben
									</MenuItem>
									<ListSubheader>Alle Modelle</ListSubheader>
									{availableModels.filter(
										(m) => m !== DEFAULT_MODEL
									).map((model) => (
										<MenuItem key={model} value={model}>
											{model}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<Link
								href='https://developers.openai.com/api/docs/pricing'
								target='_blank'
								rel='noopener'
							>
								Modell-Pricing
							</Link>
						</Stack>
						{aiModel === 'other' && (
							<TextField
								type='text'
								placeholder={`z.B. ${DEFAULT_MODEL}`}
								value={customAIModel}
								onChange={(e) =>
									setCustomAIModel(e.target.value)
								}
								sx={{ maxWidth: 'sm' }}
								disabled={isSearching}
							/>
						)}
					</>
				)}
			</Stack>
			<Button
				variant={'contained'}
				sx={{ mb: 2, mt: 2 }}
				onClick={handleSearch}
				disabled={!canSearch}
			>
				Selektoren Suchen{' '}
				{isSearching && <CircularProgress size={20} sx={{ ml: 2 }} />}
			</Button>
			{(conversationState !== undefined || chatMessages.length > 0) && (
				<Chat
					chatStatus={
						conversationState === undefined
							? ConversationState.Other
							: conversationState
					}
					chatMessages={chatMessages}
					onSendUserMessage={useAI ? handleUserMessage : undefined}
					onPauseConversation={
						useAI ? handlePauseConversation : undefined
					}
					sx={{ marginBottom: 3 }}
				/>
			)}
			<Stack sx={{ flex: 1, width: '100%' }}>
				<Button
					onClick={() => testAllSelectors()}
					sx={{ justify: 'flex-end' }}
				>
					Aktuelle Selektoren testen{' '}
					{isGettingSomeSelectorValue && (
						<CircularProgress size={20} sx={{ ml: 2 }} />
					)}
				</Button>
			</Stack>
			<Grid
				container
				spacing={1}
				sx={{
					'& .MuiFormControl-root': { width: '100%' },
					'& .MuiInputBase-root': {
						color: '#000000de',
						backgroundColor: '#0000000a !important',
						'& input': { WebkitTextFillColor: 'black' }
					}
				}}
			>
				<Grid item xs={6}>
					{SELECTORS.map((selector) => (
						<AutocompleteInput
							key={selector}
							label={`Selektor für ${toDisplayName(selector)}`}
							source={selector}
							freeSolo
							choices={
								selectorHistory.hasOwnProperty(selector)
									? selectorHistory[selector]
									: []
							}
							onCreate={(v) => {
								if (v === undefined || v === '') {
									return;
								}
								setSelector(selector, v);
							}}
							onChange={(newValue) =>
								void getSelectedValue(selector, newValue)
							}
						/>
					))}
					{/* <TextInput label="Selektor für Suggested Retail Price (UVP / AVP)" source="selectorSrp" />*/}
					{/*            <TextInput label="Selektor für Amount" source="selectorAmount" />
                    <TextInput label="Selektor für NormalizedPrice" source="selectorNormalizedPrice" />
                    <TextInput label="Selektor für Unit" source="selectorUnit" />
                    <TextInput label="Selektor für Multiplier" source="selectorMultiplier" />
                    <TextInput label="Selektor für ContainerAmount" source="selectorContainerAmount" />*/}
				</Grid>
				<Grid item xs={6}>
					{ACTUAL_VALUES.map((value) => (
						<TextInput
							key={value}
							label={`Wert für ${toDisplayName(value)}`}
							source={value}
							disabled
						/>
					))}
					{/* <TextInput label="Wert für Suggested Retail Price (UVP / AVP)" source="expectedSrp" />*/}
					{/*            <TextInput label="Wert für Amount" source="expectedAmount" />
                    <TextInput label="Wert für NormalizedPrice" source="expectedNormalizedPrice" />
                    <TextInput label="Wert für Unit" source="expectedUnit" />
                    <TextInput label="Wert für Multiplier" source="expectedMultiplier" />
                    <TextInput label="Wert für ContainerAmount" source="expectedContainerAmount" />*/}
				</Grid>
			</Grid>
			<Typography variant='body1' sx={{ marginBottom: 1 }}>
				Die Selektoren können im 5. Schritt nochmal mit einer anderen
				Methode gesucht werden.
			</Typography>
		</>
	);
};
